import React, { memo, useEffect, useRef, useState } from 'react';
import { ThumbsUp, ThumbsDown, Trash2, Pencil, Check, X, Award, Lock } from 'lucide-react';
import { ui, FLAG_TONE, FLAG_DOT, FLAG_LABELS } from '../ui';
import useEntrySettled from '../hooks/useEntrySettled';

// Kept for the answer-key panel, which renders a flag inline.
export { FLAG_LABELS };
export const FLAG_STYLES = FLAG_TONE;

/** A 6 px dot in the semantic colour, then the flag word in the same colour. */
export function FlagMark({ flag, className = '' }) {
  if (!flag) return null;
  const tone = FLAG_TONE[flag] || FLAG_TONE.abn;
  const dot = FLAG_DOT[flag] || FLAG_DOT.abn;
  return (
    <span className={`flag-mark inline-flex items-center gap-1 align-middle text-meta font-medium ${tone} ${className}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {FLAG_LABELS[flag] || 'ABN'}
    </span>
  );
}

// Net vote score colours the surface, one shade per vote up to VOTE_FULL:
// white mixed with the semantic green (up) or red (down), 10% at ±1 and 36%
// at ±5. The number carries the magnitude beyond that.
export const VOTE_FULL = 5;
const GREEN = [46, 139, 87];
const RED = [209, 67, 67];
export function voteSurface(net) {
  if (!net) return null;
  const level = Math.min(VOTE_FULL, Math.abs(net));
  const mix = 0.10 + 0.065 * (level - 1);
  const [r, g, b] = (net > 0 ? GREEN : RED).map((c) => Math.round(255 - (255 - c) * mix));
  return `rgb(${r}, ${g}, ${b})`;
}

// Probability slider: local state while dragging, debounced commit.
function ProbabilityControl({ item, revealed, readOnly, onSet }) {
  const mine = item.prob?.mine;
  const [local, setLocal] = useState(null);
  const commitTimer = useRef(null);
  useEffect(() => () => clearTimeout(commitTimer.current), []);

  const value = local ?? mine ?? 50;
  const handleChange = (v) => {
    setLocal(v);
    clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => onSet(v), 350);
  };

  const { n, median, min, max } = item.prob || {};
  return (
    <div className="mt-2 pt-2 border-t border-line">
      {!readOnly && (
        <div className="flex items-center gap-2">
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={value}
            onChange={(e) => handleChange(Number(e.target.value))}
            className="flex-1 accent-accent h-1.5"
            aria-label={`Your probability estimate for ${item.text}`}
          />
          <span className={`text-meta font-medium w-14 text-right ${mine == null && local == null ? 'text-ink-2' : 'text-accent'}`}>
            {mine == null && local == null ? 'Not set' : `Me: ${value}%`}
          </span>
        </div>
      )}
      <p className="text-meta text-ink-2 mt-1">
        {revealed
          ? (n ? `Group: median ${median}% · n=${n}${n > 1 ? ` · range ${min}–${max}%` : ''}` : 'No estimates yet')
          : 'Group estimates hidden until reveal'}
      </p>
    </div>
  );
}

/**
 * One entry on a board or in a grid.
 *
 * White surface, hairline border, no fill. State is a 3 px left rule only:
 * none for a pending student entry, accent for a revealed finding (and for
 * the final diagnosis), a faint accent rule for a private individual-phase
 * entry. Inside, top to bottom: the label (what was typed, or the finding's
 * canonical name), the finding text at 15/600 where there is one, then the
 * author with the vote control right-aligned on the same row.
 *
 * Management icons live in `.card-tools` and appear on hover or keyboard
 * focus (or a long-press on touch). Presenter mode hides them, and the
 * author and vote control, via CSS on the root's data attribute.
 */
const ItemCard = memo(function ItemCard({
  item, onVote, onDelete, onEdit, canModify,
  isSmall = false,
  emphasis = false, // ranked diagnosis names read at 15/600
  revealed = true, readOnly = false, probability = null,
  onOverride = null, // facilitator match correction (case flow, post-reveal)
  unmatchedLabel = 'Negative / not a feature of this case',
  // Vote-scale props are accepted for API compatibility; the card no longer
  // fills by score. The count and the user's own filled thumb carry it.
  maxVotes, minVotes, isGlobal, // eslint-disable-line no-unused-vars
}) {
  const isPrivate = !!item.private;
  const [editing, setEditing] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const pressTimer = useRef(null);
  const settled = useEntrySettled();
  const matched = item.matchState === 'matched';
  const unmatched = item.matchState === 'unmatched';
  const myVote = item.myVote || 0;
  const displayTitle = item.matchTitle || item.text;
  const details = item.matchDetails || [];
  const multiHit = details.length > 1;

  // Net vote score colours the surface (fixed tint, the number carries the
  // magnitude) and takes over the left rule: the vote is the newer signal
  // than the reveal. Hidden under blind voting, where the tally is secret.
  const net = revealed && !isPrivate ? (item.votes || 0) : 0;
  const voteTone = net > 0
    ? 'border-l-[3px] border-l-flag-green'
    : net < 0
      ? 'border-l-[3px] border-l-flag-red'
      : null;
  const rule = voteTone || (
    item.correctDx || matched
      ? 'bg-surface border-l-[3px] border-l-accent'
      : isPrivate
        ? 'bg-surface border-l-[3px] border-l-accent/40'
        : 'bg-surface'
  );
  const state = item.correctDx ? 'final' : matched ? 'matched' : unmatched ? 'unmatched' : isPrivate ? 'private' : 'pending';
  const voteState = net > 0 ? 'up' : net < 0 ? 'down' : 'none';
  const voteLevel = Math.min(VOTE_FULL, Math.abs(net));
  const surface = net ? { backgroundColor: voteSurface(net) } : undefined;

  const finishEdit = (value) => {
    setEditing(false);
    const trimmed = (value || '').trim();
    if (trimmed && trimmed !== item.text) onEdit(trimmed);
  };

  // Touch has no hover: a long-press (500 ms) reveals the management icons.
  const pressStart = () => { pressTimer.current = setTimeout(() => setToolsOpen((v) => !v), 500); };
  const pressEnd = () => clearTimeout(pressTimer.current);

  const voteBtn = 'vote-btn inline-flex items-center justify-center w-6 h-6 rounded-ctl text-ink-2 hover:bg-accent-tint hover:text-ink transition-colors';

  return (
    <div
      className={`item-enter ${settled} group card border-line rounded-card transition-colors duration-200 ${rule} ${isSmall ? 'p-3 mb-2' : 'px-3.5 py-3 mb-2'} ${unmatched ? 'text-ink-2' : ''}`}
      data-state={state}
      data-vote={voteState}
      data-vote-level={voteLevel}
      style={surface}
      onTouchStart={pressStart}
      onTouchEnd={pressEnd}
      onTouchMove={pressEnd}
    >
      <div className="flex items-start gap-2">
        {editing ? (
          <input
            autoFocus
            defaultValue={item.text}
            onKeyDown={(e) => {
              if (e.key === 'Enter') finishEdit(e.target.value);
              if (e.key === 'Escape') setEditing(false);
            }}
            onBlur={() => setEditing(false)}
            className={`${ui.input} h-8 flex-1`}
            aria-label="Edit item text"
          />
        ) : (
          <div className="flex-1 min-w-0">
            {/* Label: what was typed, or the finding's canonical name once
                revealed. With a finding text beneath it reads as the caption;
                on a plain entry it is the entry. */}
            <p className={`break-words ${
              matched ? 'text-meta font-medium text-ink-2'
                : emphasis ? 'text-body font-semibold text-ink'
                  : 'text-body text-ink'
            } ${unmatched ? 'text-ink-2' : ''}`}>
              {displayTitle}
              {item.correctDx && (
                <span className={`${ui.chipAccent} ml-2 align-middle`}>
                  <Award className="w-3 h-3" />Final diagnosis
                </span>
              )}
            </p>
            {/* The patient's actual values: the point of a reveal, so they
                carry the card's weight. */}
            {matched && (
              <div className="mt-0.5 space-y-0.5">
                {details.map((d, i) => (
                  <p key={i} className="text-body font-semibold text-ink leading-snug">
                    {multiHit && d.name && <span className="font-medium text-ink-2">{d.name}: </span>}
                    {d.text}
                    {d.flag && <FlagMark flag={d.flag} className="ml-2" />}
                  </p>
                ))}
                {!details.length && <p className="text-body font-semibold text-ink">Present in this case</p>}
              </div>
            )}
            {unmatched && <p className="text-meta text-ink-2 italic mt-0.5">{unmatchedLabel}</p>}
          </div>
        )}
        <span className={`card-tools flex gap-0.5 flex-shrink-0 items-center -mr-1 ${toolsOpen ? 'is-open' : ''}`}>
          {onOverride && item.matchState && (
            <button
              onClick={() => onOverride(!matched)}
              className={`${ui.iconBtn} w-7 h-7`}
              title={matched ? 'Override: mark as NOT a finding in this case' : 'Override: mark as a real finding in this case'}
              aria-label={`Override match for "${displayTitle}"`}
            >
              {matched ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
            </button>
          )}
          {canModify && !readOnly && !editing && (
            <>
              <button onClick={() => setEditing(true)} className={`${ui.iconBtn} w-7 h-7`} aria-label={`Edit "${displayTitle}"`} title="Edit">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button onClick={onDelete} className={`${ui.iconBtn} w-7 h-7 hover:text-flag-red`} aria-label={`Delete "${displayTitle}"`} title="Remove (you can undo)">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </span>
      </div>

      <div className="flex justify-between items-center mt-1.5">
        <span className="author text-meta text-ink-2 flex items-center gap-2 min-w-0">
          {isPrivate ? (
            <span className={ui.chip} title="Only you can see this until the facilitator merges everyone's entries">
              <Lock className="w-3 h-3" />Only you
            </span>
          ) : (
            <span className="truncate">{item.addedBy}</span>
          )}
          {item.listedBy > 1 && (
            <span title={`Listed independently by ${item.listedBy} students in the individual phase${revealed ? `: counts as ${item.listedBy - 1} ${item.listedBy - 1 === 1 ? 'vote' : 'votes'}` : ''}`}>
              {item.listedBy} listed
            </span>
          )}
        </span>
        {/* Nothing to vote on until the entry is shared with the class. */}
        {!isPrivate && (
          <div className="vote-control flex items-center gap-1 flex-shrink-0">
            {!readOnly && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(1); }}
                className={`${voteBtn} ${myVote > 0 ? 'text-accent' : ''}`}
                aria-label={`Upvote "${displayTitle}"`}
                title="Upvote (+1)"
              >
                <ThumbsUp className="w-4 h-4" fill={myVote > 0 ? 'currentColor' : 'none'} />
              </button>
            )}
            <span
              className={`inline-flex items-center gap-1 text-meta font-medium text-center min-w-[1.25rem] justify-center ${revealed ? 'text-ink' : 'text-ink-2'}`}
              aria-live="polite"
              aria-label={revealed ? `${item.votes || 0} votes` : 'Votes hidden until reveal'}
              title={revealed
                ? `${item.votes || 0} net${item.mergeVotes ? `, ${item.mergeVotes} of them from students who listed it independently` : ''}`
                : 'Votes hidden until the facilitator reveals them'}
            >
              {revealed ? (item.votes || 0) : '–'}
              {myVote !== 0 && (
                <span
                  className="inline-block w-1.5 h-1.5 rounded-full bg-accent"
                  title={`Your vote: ${myVote > 0 ? '+' : ''}${myVote}`}
                  aria-label={`Your vote: ${myVote > 0 ? '+' : ''}${myVote}`}
                />
              )}
            </span>
            {!readOnly && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onVote(-1); }}
                className={`${voteBtn} ${myVote < 0 ? 'text-accent' : ''}`}
                aria-label={`Downvote "${displayTitle}"`}
                title="Downvote (-1)"
              >
                <ThumbsDown className="w-4 h-4" fill={myVote < 0 ? 'currentColor' : 'none'} />
              </button>
            )}
          </div>
        )}
      </div>
      {probability?.enabled && !isPrivate && (
        <ProbabilityControl item={item} revealed={revealed} readOnly={readOnly} onSet={probability.onSet} />
      )}
    </div>
  );
});

export default ItemCard;
